import { usuarioPadraoDoSlug } from '@nyroh/contracts';
import { api, enviarImagem, ErroApiNyroh } from './api';

/**
 * ============================================================================
 * CAMADA DE DADOS DA FÁBRICA
 * ============================================================================
 *
 * Substitui `lib/supabase.ts` mantendo os MESMOS nomes exportados, para que
 * `index.tsx`, `CostDashboardView.tsx` e `VisualEditor.tsx` continuem
 * funcionando sem reescrita.
 *
 * A mudança de fundo: o navegador não fala mais com banco. E o banco da fábrica
 * — que tinha uma coluna `password` em texto puro, com default '123', baixada
 * para o navegador a cada carregamento (falha S-10) — deixou de existir como
 * destino separado.
 */

/**
 * Mantido para os 12 pontos de chamada que testam esta flag.
 *
 * Hoje é sempre `true`: se a configuração faltasse, `exigirEnv` em `api.ts` já
 * teria derrubado o módulo com uma mensagem na tela. A flag existia para o modo
 * "sem banco", que deixou de existir quando o fallback de credencial saiu.
 */
export const isSupabaseConfigured = true;

/** Compatibilidade: quem só testava `if (supabase)` continua compilando. */
export const supabase = { configurado: true } as const;

export const uploadImage = async (file: File): Promise<string | null> => {
  try {
    return await enviarImagem(file);
  } catch (erro) {
    // A mensagem real chega ao operador em vez do antigo "verifique se o bucket
    // uploads está criado e público" — instrução que, além de errada agora, era
    // exatamente a configuração que abria o bucket para escrita anônima.
    console.error('Falha ao enviar imagem:', erro);
    alert(erro instanceof Error ? erro.message : 'Erro ao enviar a imagem.');
    return null;
  }
};

/**
 * ----------------------------------------------------------------------------
 * REGISTRO DE CUSTO DE GERAÇÃO
 * ----------------------------------------------------------------------------
 *
 * Antes era a tabela `ditho_generation_costs_log`, escrita pelo navegador.
 * Agora é uma configuração JSON na API — mesma informação, sem o navegador
 * segurando credencial de escrita no banco.
 *
 * Quando a geração passar a rodar no worker (job `gerar_site`), este registro
 * vira subproduto do job e este caminho some.
 */
export interface RegistroCusto {
  id: string;
  timestamp: number;
  clientName: string;
  status: 'success' | 'failed';
  cost: number;
}

const TIPO_CUSTO = 'custo-geracao';

export const saveCostLog = async (entry: RegistroCusto): Promise<boolean> => {
  try {
    await api.chamar('configsJson.salvar', {
      corpo: {
        id: entry.id,
        nome: entry.clientName,
        tipo: TIPO_CUSTO,
        conteudo: entry,
      },
    });
    return true;
  } catch (erro) {
    console.error('Falha ao registrar custo de geração:', erro);
    return false;
  }
};

export const fetchCostLogs = async (): Promise<RegistroCusto[] | null> => {
  try {
    const todas = await api.chamar('configsJson.listar', {});
    return todas
      .filter((c) => c.tipo === TIPO_CUSTO)
      .map((c) => c.conteudo as RegistroCusto)
      .sort((a, b) => b.timestamp - a.timestamp);
  } catch (erro) {
    console.error('Falha ao buscar custos:', erro);
    return null;
  }
};

export const clearCostLogs = async (): Promise<boolean> => {
  try {
    const todas = await api.chamar('configsJson.listar', {});
    for (const c of todas.filter((x) => x.tipo === TIPO_CUSTO)) {
      await api.chamar('configsJson.excluir', { params: { id: c.id } });
    }
    return true;
  } catch (erro) {
    console.error('Falha ao limpar custos:', erro);
    return false;
  }
};

/**
 * ----------------------------------------------------------------------------
 * CLIENTES DA FÁBRICA
 * ----------------------------------------------------------------------------
 */

/** Forma que `index.tsx` já usa. Mantida para não reescrever a tela agora. */
export interface ClienteFabrica {
  id: string;
  name: string;
  slug: string;
  industry?: string;
  scope?: string;
  status?: string;
  payment_status?: string;
  subdomain?: string;
  created_at?: number;
  trial_hours?: number;
  payment_link?: string;
  site_content?: string;
  theme_config?: unknown;
  lead_data?: unknown;
  email?: string;
  /**
   * NUNCA mais preenchido.
   *
   * A consulta antiga selecionava `email, password` de TODOS os clientes do dia
   * e baixava para o navegador; o login comparava em memória. A coluna tinha
   * default '123'. O campo continua no tipo só para as telas compilarem, e vem
   * sempre `undefined`.
   */
  password?: undefined;
}

export async function listarClientes(opcoes: {
  de?: number;
  ate?: number;
  limite?: number;
} = {}): Promise<ClienteFabrica[]> {
  const pagina = await api.chamar('clientes.listar', {
    query: {
      limite: Math.min(opcoes.limite ?? 200, 200),
      periodo: opcoes.de || opcoes.ate ? 'custom' : 'todos',
      de: opcoes.de ? new Date(opcoes.de).toISOString().slice(0, 10) : undefined,
      ate: opcoes.ate ? new Date(opcoes.ate).toISOString().slice(0, 10) : undefined,
      comDadosNegocio: false,
    },
  });

  return pagina.itens.map((c) => ({
    id: c.id,
    name: c.nome,
    slug: c.slug,
    industry: c.categoria ?? undefined,
    status: c.status,
    payment_status: c.status === 'ativo' ? 'paid' : 'pending',
    subdomain: c.dominioPrincipal ?? undefined,
    created_at: new Date(c.criadoEm).getTime(),
    email: c.email ?? undefined,
    password: undefined,
  }));
}

/**
 * Salva o cliente. `site_content` NÃO vai para o banco.
 *
 * O HTML gerado é publicado como artefato no R2, com o spec guardado no
 * Postgres. É a mesma decisão da seção 08 do diagnóstico, aplicada à fábrica:
 * o banco desta aplicação já foi abandonado uma vez por estourar cota de
 * tamanho, exatamente por guardar HTML.
 */
export async function salvarCliente(c: ClienteFabrica & { site_content?: string }): Promise<string> {
  let clienteId = c.id;

  try {
    await api.chamar('clientes.obter', { params: { id: c.id } });
  } catch (erro) {
    if (!(erro instanceof ErroApiNyroh && erro.naoEncontrado)) throw erro;
    const criado = await api.chamar('clientes.criar', {
      corpo: {
        nome: c.name,
        slug: c.slug,
        email: c.email,
        categoria: c.industry,
        status: c.payment_status === 'paid' ? 'ativo' : 'pendente',
        // Sem `senha`: o servidor usa a senha padrao e marca a troca como
        // obrigatoria. O usuario sai de `usuarioPadraoDoSlug` — usar `c.slug`
        // cru gravava um usuario COM hifens, e o painel pre-preenche a tela de
        // login com a versao sem: a conta existia e o dono nao entrava.
        credencial: { usuario: usuarioPadraoDoSlug(c.slug) },
      },
    });
    clienteId = criado.id;
  }

  if (clienteId === c.id) {
    await api.chamar('clientes.atualizar', {
      params: { id: clienteId },
      corpo: {
        nome: c.name,
        slug: c.slug,
        categoria: c.industry,
        status: c.payment_status === 'paid' ? 'ativo' : 'pendente',
      },
    });
  }

  if (c.site_content) {
    await publicarHtml(clienteId, c.slug, c.name, c.site_content);
  }

  return clienteId;
}

/** Publica HTML pronto como artefato no R2, criando o site se preciso. */
export async function publicarHtml(
  clienteId: string,
  slug: string,
  nome: string,
  html: string,
): Promise<void> {
  const spec = {
    versaoSpec: 1 as const,
    template: 'html-importado',
    // Sem efeito neste caminho: a unica secao e `htmlBruto` com o documento
    // completo, que o renderizador serve como esta.
    leiaute: 'classico' as const,
    negocio: { nome, categoria: '', telefone: '', whatsapp: '', endereco: '' },
    paleta: {
      primaria: '#1F4E79',
      secundaria: '#16324F',
      fundo: '#FFFFFF',
      texto: '#1A1A1A',
    },
    tipografia: { titulo: 'Inter', corpo: 'Inter', escala: 'normal' as const },
    seo: { titulo: nome, descricao: '', palavrasChave: [], indexavel: true },
    secoes: [{ tipo: 'htmlBruto' as const, html, origem: 'importado' as const }],
    cssExtra: '',
  };

  const sites = await api.chamar('sites.listarPorCliente', { params: { id: clienteId } });
  const site =
    sites[0] ??
    (await api.chamar('sites.criar', { corpo: { clienteId, slug, spec } }));

  await api.chamar('sites.publicar', {
    params: { id: site.id },
    corpo: { spec, geradoPor: 'fabrica', apenasRascunho: false },
  });
}

export async function excluirClientes(ids: string[]): Promise<number> {
  const r = await api.chamar('clientes.excluir', { corpo: { ids } });
  return r.excluidos;
}

export async function excluirCliente(id: string): Promise<number> {
  return excluirClientes([id]);
}

/**
 * Carrega um cliente com tudo: dados de negócio e o HTML publicado.
 *
 * O HTML vem do R2, não do banco. A versão antiga fazia `select('*')` e trazia
 * `site_content` junto — a coluna que fez o banco anterior desta aplicação
 * estourar a cota de tamanho.
 */
export async function obterClienteCompleto(id: string): Promise<
  (ClienteFabrica & { site_content: string; lead_data: unknown }) | null
> {
  try {
    const c = await api.chamar('clientes.obter', { params: { id } });

    let html = '';
    if (c.siteId) {
      const site = await api.chamar('sites.obter', { params: { id: c.siteId } });
      if (site.versaoAtualId) {
        const versoes = await api.chamar('sites.listarVersoes', { params: { id: c.siteId } });
        const corrente = versoes.find((v) => v.id === site.versaoAtualId);
        if (corrente) {
          const cdn = (
            await import('./api')
          ).CDN_ASSETS.replace(/\/nyroh-assets\/?$/, '/nyroh-sites');
          const r = await fetch(`${cdn}/${corrente.chaveR2}`);
          if (r.ok) html = await r.text();
        }
      }
    }

    return {
      id: c.id,
      name: c.nome,
      slug: c.slug,
      industry: c.categoria ?? undefined,
      status: c.status,
      payment_status: c.status === 'ativo' ? 'paid' : 'pending',
      subdomain: c.dominioPrincipal ?? undefined,
      created_at: new Date(c.criadoEm).getTime(),
      email: c.email ?? undefined,
      password: undefined,
      site_content: html,
      lead_data: c.dadosNegocio,
    };
  } catch (erro) {
    if (erro instanceof ErroApiNyroh && erro.naoEncontrado) return null;
    throw erro;
  }
}

/**
 * Checagem de duplicados por nome e telefone.
 *
 * A versão antiga montava um filtro PostgREST na mão, concatenando nomes dentro
 * de aspas — `name.in.("Fulano","Beltrano")`. Um nome com aspas ou vírgula
 * quebrava a query, e um nome com sintaxe de filtro dentro dela podia mudar o
 * significado da consulta. Aqui os termos vão como PARÂMETRO, e quem monta o SQL
 * é o Prisma.
 */
export async function verificarDuplicados(
  itens: Array<{ name: string; phone: string }>,
): Promise<Array<{ name: boolean; phone: boolean; id: string }>> {
  if (itens.length === 0) return [];

  const resultados: Array<{ name: boolean; phone: boolean; id: string }> = [];

  // Uma busca por item, com limite pequeno. Numa importação grande vale trocar
  // por um endpoint dedicado que receba o lote — está anotado como melhoria,
  // não como correção pendente: a consulta atual usa índice.
  for (const item of itens) {
    const termo = item.name.trim() || item.phone.replace(/\D/g, '');
    if (!termo) {
      resultados.push({ name: false, phone: false, id: '' });
      continue;
    }
    const pagina = await api.chamar('clientes.listar', {
      query: { busca: termo, limite: 5, periodo: 'todos', comDadosNegocio: false },
    });
    const telefoneLimpo = item.phone.replace(/\D/g, '');
    const porNome = pagina.itens.find(
      (c) => c.nome.trim().toLowerCase() === item.name.trim().toLowerCase(),
    );
    const porTelefone = telefoneLimpo
      ? pagina.itens.find((c) => (c.telefone ?? '').replace(/\D/g, '') === telefoneLimpo)
      : undefined;

    resultados.push({
      name: Boolean(porNome),
      phone: Boolean(porTelefone),
      id: porNome?.id ?? porTelefone?.id ?? '',
    });
  }

  return resultados;
}

/** Existe cliente com este telefone? */
export async function existeTelefone(telefone: string): Promise<boolean> {
  const limpo = telefone.replace(/\D/g, '');
  if (!limpo) return false;
  const pagina = await api.chamar('clientes.listar', {
    query: { busca: limpo, limite: 1, periodo: 'todos', comDadosNegocio: false },
  });
  return pagina.itens.length > 0;
}

/**
 * ----------------------------------------------------------------------------
 * LOGIN DO CLIENTE FINAL
 * ----------------------------------------------------------------------------
 *
 * O que isto substitui, textualmente:
 *
 *     const foundClient = clients.find(
 *       c => c.email === email && c.password === password
 *     );
 *
 * A comparação acontecia na MEMÓRIA DO NAVEGADOR, contra uma lista de e-mails e
 * senhas em texto puro que o app baixava a cada carregamento. Qualquer pessoa
 * com o DevTools aberto via a credencial de todos os clientes.
 */
export async function entrarComoCliente(
  usuario: string,
  pin: string,
): Promise<{ clienteId: string; slug: string; nome: string } | null> {
  try {
    const sessao = await api.chamar('portal.entrar', { corpo: { usuario, pin } });
    return { clienteId: sessao.clienteId, slug: sessao.slug, nome: sessao.nome };
  } catch (erro) {
    if (erro instanceof ErroApiNyroh && (erro.precisaAutenticar || erro.status === 429)) {
      return null;
    }
    throw erro;
  }
}
